import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtNum } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

const TITLES = {
  "broker-accounts": {
    title: "Broker Accounts",
    subtitle: "Per-policy broker reconciliation history",
  },
  "master-policy": {
    title: "Master Policy",
    subtitle: "Master GL/PA certificates with rates",
  },
  siriuspoint: {
    title: "SiriusPoint Data",
    subtitle: "Blanket Accident Program policies",
  },
  "tax-rates": {
    title: "State Tax Rates",
    subtitle: "Premium tax / SL tax / stamping fee references",
  },
  "commission-notes": {
    title: "Commission Notes",
    subtitle: "Carrier commission rate documentation",
  },
};

function fmtCell(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try {
      return new Date(v).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
    } catch {
      return v;
    }
  }
  if (typeof v === "number") {
    if (Number.isInteger(v) && Math.abs(v) < 100000) return v.toString();
    return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
  return String(v);
}

function snakeToTitle(s) {
  return s
    .replace(/_/g, " ")
    .replace(/\bnum\b/gi, "#")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function Reference() {
  const { kind } = useParams();
  const meta = TITLES[kind] || { title: kind, subtitle: "" };
  const [data, setData] = useState({ items: [], total: 0, columns: [] });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPage(1);
    setSearch("");
  }, [kind]);

  useEffect(() => {
    setLoading(true);
    const params = { page, page_size: pageSize };
    if (search.trim()) params.search = search.trim();
    api
      .get(`/reference/${kind}`, { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [kind, page, pageSize, search]);

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const visibleCols = useMemo(() => data.columns.slice(0, 14), [data.columns]);

  return (
    <div className="space-y-4 max-w-[1500px]" data-testid={`reference-${kind}-page`}>
      <Card className="p-5 border-slate-200">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Reference</div>
            <h2 className="text-lg font-semibold tracking-tight">{meta.title}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{meta.subtitle}</p>
          </div>
          <div className="relative w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid={`reference-search-${kind}`}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search…"
              className="pl-9 h-9"
            />
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
          <div className="text-[12px] text-slate-600">
            <span className="font-medium text-slate-900">{fmtNum(data.total)}</span> rows
          </div>
          <div className="flex items-center gap-2 text-[12px] text-slate-500">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              data-testid={`reference-${kind}-prev`}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span>
              Page <b>{page}</b> / {totalPages}
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              data-testid={`reference-${kind}-next`}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                {visibleCols.map((c) => (
                  <th key={c} className="px-4 py-3 font-medium whitespace-nowrap">
                    {snakeToTitle(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={visibleCols.length} className="py-12 text-center text-slate-400 text-sm">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr>
                  <td colSpan={Math.max(1, visibleCols.length)} className="py-12 text-center text-slate-400 text-sm">
                    No rows.
                  </td>
                </tr>
              )}
              {!loading &&
                data.items.map((row, i) => (
                  <tr key={row.id || i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                    {visibleCols.map((c, j) => (
                      <td
                        key={c}
                        className={`px-4 py-2.5 ${j === 0 ? "text-slate-900 font-medium" : "text-slate-600"} whitespace-nowrap max-w-[260px] truncate`}
                        title={String(row[c] ?? "")}
                      >
                        {fmtCell(row[c])}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
